"use client";

import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { useTheme } from "../../Components/ThemeContext";

const lightGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

const darkGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(251,146,60,0.05) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

const WhoWeAre = () => {
  const { isDark } = useTheme();

  const fadeUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  const staggerContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.2 } },
  };

  // Theme values
  const headingText  = isDark ? "text-white"     : "text-gray-900";
  const greenHeading = isDark ? "text-green-400" : "text-green-700";
  const descText     = isDark ? "text-gray-400"  : "text-gray-700";
  const listText     = isDark ? "text-gray-300"  : "text-gray-900";
  const expBoxBg     = isDark ? "bg-emerald-700/20" : "bg-[#1a2e1a]";
  const expBoxBorder = isDark ? "" : "border border-green-900/40";

  // SEO-rich checklist — each item uses a real keyword phrase
  const highlights = [
    "Licensed & regulated software company",
    "Dedicated professional support team",
    "Comprehensive technical analysis",
    "Optimised web & mobile tech solutions",
    "Premium UK technology partner",
  ];

  return (
    <motion.section
      aria-labelledby="who-we-are-heading"
      className={`relative w-full overflow-hidden pb-8 pt-0 px-6 md:px-12 lg:px-20 transition-colors duration-300 ${
        isDark ? "bg-black text-white" : "bg-transparent text-gray-900"
      }`}
      style={isDark ? darkGridStyle : lightGridStyle}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={staggerContainer}
    >
      {/* Decorative glows — light mode only, hidden from screen readers */}
      {!isDark && (
        <div
          className="pointer-events-none absolute inset-0 overflow-hidden"
          aria-hidden="true"
          style={{ zIndex: 0 }}
        >
          <div
            style={{
              position: "absolute", top: "0%", left: "-10%",
              width: "45vw", height: "45vw",
              background: "radial-gradient(circle, rgba(74,222,128,0.10) 0%, transparent 70%)",
              filter: "blur(45px)",
            }}
          />
          <div
            style={{
              position: "absolute", top: "20%", right: "-8%",
              width: "40vw", height: "40vw",
              background: "radial-gradient(circle, rgba(251,146,60,0.08) 0%, transparent 70%)",
              filter: "blur(45px)",
            }}
          />
        </div>
      )}

      <div className="relative max-w-7xl mx-auto" style={{ zIndex: 1 }}>

        {/* ── Section heading ── */}
        <motion.div className="text-center mb-10" variants={fadeUp}>
          {/*
           * Use a <p> for the eyebrow label so the <h2> below is the real
           * keyword-rich heading crawlers index.
           */}
          <p className={`text-xl sm:text-2xl lg:text-3xl font-semibold ${headingText} mb-4`}>
            Who We Are
          </p>
          <h2
            id="who-we-are-heading"
            className={`text-2xl sm:text-3xl md:text-4xl font-bold ${greenHeading}`}
          >
            Empowering Innovations Together
          </h2>
        </motion.div>

        {/* ── Two-column layout ── */}
        <motion.div
          className="flex flex-col md:flex-row items-start md:items-center gap-10"
          variants={staggerContainer}
        >

          {/* Images column */}
          <motion.div
            className="flex flex-row gap-4 w-full md:w-1/2"
            variants={fadeUp}
          >
            {/* Primary image */}
            <div className="w-1/2 flex-shrink-0">
              <motion.img
                src="/Images/developer.png"
                alt="Fiieve software developer working on a web development project"
                className="rounded-2xl object-cover w-full h-52 sm:h-60 md:h-72 lg:h-80 shadow-lg"
                loading="lazy"
                width={400}
                height={320}
                variants={fadeUp}
              />
            </div>

            {/* Stats box + secondary image */}
            <div className="flex flex-col justify-between gap-4 w-1/2">
              <motion.div
                className={`flex flex-col justify-center ${expBoxBg} ${expBoxBorder} rounded-2xl p-4 sm:p-5 w-full h-24 sm:h-28 md:h-32 text-center shadow-lg`}
                variants={fadeUp}
              >
                {/* Marked up with <strong> so the stat carries semantic weight */}
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-white">
                  <strong>+5 Years</strong>
                </p>
                <p className="text-gray-300 text-sm sm:text-base mt-1">
                  Industry Experience
                </p>
              </motion.div>

              <motion.img
                src="/Images/developer.png"
                alt="Fiieve team collaborating on mobile app and AI automation solutions"
                className="rounded-2xl object-cover w-full h-32 sm:h-36 md:h-44 shadow-lg"
                loading="lazy"
                width={200}
                height={176}
                variants={fadeUp}
              />
            </div>
          </motion.div>

          {/* Text column */}
          <motion.div
            className="w-full md:w-1/2 space-y-5 text-center md:text-left"
            variants={fadeUp}
          >
            {/* h3 is fine here — it sits beneath the section h2 */}
            <h3 className={`text-xl sm:text-2xl font-semibold ${headingText}`}>
              About Fiieve — UK Software &amp; Engineering Company
            </h3>

<p className={`${descText} leading-relaxed text-sm sm:text-base`}>
  Fiieve is a UK-based software company delivering <strong>web development</strong>,{" "}
  <strong>mobile apps</strong>, <strong>AI automation</strong>,{" "}
  <strong>UI/UX design</strong>, and <strong>IT consultancy</strong> services.
</p>

            {/* Keyword-rich checklist */}
            <ul className="space-y-3" aria-label="Fiieve company highlights">
              {highlights.map((item, index) => (
                <motion.li
                  key={index}
                  className="flex items-center justify-center md:justify-start gap-3 text-sm sm:text-base"
                  variants={fadeUp}
                >
                  <CheckCircle2
                    className={`w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 ${
                      isDark ? "text-green-400" : "text-[#1a4d1a]"
                    }`}
                    aria-hidden="true"
                  />
                  <span className={listText}>{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default WhoWeAre;